import type { EventStore } from '@application/ports.ts'
import { StoredEventSchema, type StoredEvent } from '@domain/events.ts'

/**
 * Leer un archivo de copia de seguridad y meterlo en el registro.
 *
 * Vive aparte porque hay dos sitios que restauran, y tienen que hacerlo igual:
 * el panel de copias del modo padres y —el que de verdad importa— la pantalla de
 * alta, para que una tablet nueva pueda empezar por la copia en vez de crear una
 * casa y encontrarse luego con dos.
 */

export type Restauracion =
  | { readonly ok: true; readonly puestos: number; readonly yaEstaban: number }
  | { readonly ok: false; readonly porque: 'no-es-json' | 'no-es-un-registro' }

export async function restaurarRegistro(store: EventStore, file: File): Promise<Restauracion> {
  let leido: unknown
  try {
    leido = JSON.parse(await file.text())
  } catch {
    // Equivocarse de archivo es lo normal, no un error que arreglar.
    return { ok: false, porque: 'no-es-json' }
  }

  const filas = (leido as { events?: unknown }).events
  if (!Array.isArray(filas)) return { ok: false, porque: 'no-es-un-registro' }

  const validos = filas.filter(
    (fila): fila is StoredEvent => StoredEventSchema.safeParse(fila).success,
  )
  const resultado = await store.importEvents(validos)
  return {
    ok: true,
    puestos: resultado.imported,
    // Lo que ya estaba más lo que no se entendió: para quien mira la pantalla es
    // lo mismo —no ha entrado— y separarlo solo confundiría.
    yaEstaban: resultado.skipped + (filas.length - validos.length),
  }
}
